import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { useCourseStudents, useRegistrationId } from "api/hooks";
import { MoveUserToCourseModal } from "components/templates/MoveUserToCourse/MoveUserToCourseModal";

import { courseRegistrationApi } from "api/registrations";
import { RetainedRevenueModal } from "components/templates/RetainedRevenue/RetainedRevenueModal";
import { routes } from "consts";
import { PaziaButton } from "components/atoms";
import { QUERIES_KEYS } from "queries";
import { useNavigate } from "react-router-dom";
import { useInavalidate, useToaster } from "utils";

interface StudentActionsTabProps {
  courseId: number;
  userId: number;
}
export const StudentActionsTab = ({ courseId, userId }: StudentActionsTabProps) => {
  var navigate = useNavigate();
  const invalidate = useInavalidate(QUERIES_KEYS.STUDENT_COURSES(userId));
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useToaster();
  const { data: registrationId, loading } = useRegistrationId(userId, courseId);
  const { students } = useCourseStudents(courseId);
  const [modalRegistration, setModalRegistration] = React.useState<number | null>(null);
  const [dismissing, setDismissing] = React.useState(false);
  const [dismissReason, setDismissReason] = React.useState("");
  const [removeModalOpen, setRemoveModalOpen] = React.useState(false);
  const { data: revenueSummary, isLoading: loadingRevenueSummary } = useQuery({
    queryKey: ["registrationRevenueSummary", courseId, registrationId],
    queryFn: () => courseRegistrationApi.getRevenueSummary(courseId, registrationId!),
    enabled: removeModalOpen && !!registrationId,
  });

  const currentStudent = students.find((s) => s.id === userId);

  const handleDismiss = async () => {
    if (!registrationId || !dismissReason.trim()) return;
    setDismissing(true);
    try {
      await courseRegistrationApi.dismissCancellationRequest(courseId, registrationId, dismissReason.trim());
      await queryClient.invalidateQueries({ queryKey: QUERIES_KEYS.COURSE_STUDENTS(courseId) });
      setDismissReason("");
    } catch (e) {
      showError("Nie udało się odrzucić zgłoszenia. Spróbuj ponownie.");
      console.error(e);
    } finally {
      setDismissing(false);
    }
  };

  if (loading) return null;
  if (!registrationId) return null;

  return (
    <>
      <div className="mt-4 space-y-6">
        {/* Header */}
        <div className="border-b border-gray-200 pb-4 dark:border-slate-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100">Akcje dla uczestnika</h3>
          <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">Zarządzaj uczestnictwem użytkownika w kursie</p>
        </div>

        {currentStudent?.cancellationRequestedAt && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-500/30 dark:bg-amber-500/10">
            <p className="text-sm font-semibold text-amber-800 dark:text-amber-300">
              Klient zgłosił chęć rezygnacji {new Date(currentStudent.cancellationRequestedAt).toLocaleDateString("pl-PL")}
            </p>
            {currentStudent.cancellationReason && (
              <p className="mt-1 text-sm text-amber-700 dark:text-amber-400">Powód: {currentStudent.cancellationReason}</p>
            )}
            <div className="mt-3 space-y-2">
              <textarea
                className="form-input w-full dark:bg-slate-800 border-border bg-survey-bg text-subheading focus:border-border-highlight block w-full rounded-md border p-2 shadow-sm focus:ring-0 sm:text-sm"
                rows={2}
                placeholder="Powód odrzucenia zgłoszenia (wymagane)"
                value={dismissReason}
                onChange={(e) => setDismissReason(e.target.value)}
              />
              <PaziaButton
                type="secondary"
                title="Odrzuć zgłoszenie"
                btnType="button"
                processing={dismissing}
                disabled={!dismissReason.trim()}
                onClick={handleDismiss}
              />
            </div>
          </div>
        )}

        {!currentStudent?.cancellationRequestedAt && currentStudent?.cancellationRejectedAt && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
            <p className="text-sm font-semibold text-gray-800 dark:text-slate-200">
              Zgłoszenie rezygnacji odrzucone {new Date(currentStudent.cancellationRejectedAt).toLocaleDateString("pl-PL")}
            </p>
            {currentStudent.cancellationRejectionReason && (
              <p className="mt-1 text-sm text-gray-700 dark:text-slate-400">Powód: {currentStudent.cancellationRejectionReason}</p>
            )}
          </div>
        )}

        {/* Action Cards */}
        <div className="grid gap-4 md:grid-cols-2">
          {/* Move User Card */}
          <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-900/40">
            <div className="flex items-start">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 dark:bg-blue-500/15">
                <svg
                  className="h-6 w-6 text-blue-600 dark:text-blue-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5"
                  />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h4 className="text-base font-semibold text-gray-900 dark:text-slate-100">Przenieś na inny kurs</h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
                  Przenieś użytkownika na inny kurs zachowując historię płatności. Możesz dostosować strukture transz i
                  przypisać istniejące płatności.
                </p>
                <div className="mt-4">
                  <PaziaButton
                    type="secondary"
                    title="Przenieś użytkownika"
                    btnType="button"
                    onClick={() => setModalRegistration(registrationId)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Delete User Card */}
          <div className="relative overflow-hidden rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-900/40">
            <div className="flex items-start">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-50 dark:bg-red-500/15">
                <svg
                  className="h-6 w-6 text-red-600 dark:text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M22 10.5h-6m-2.25-4.125c0-.621-.504-1.125-1.125-1.125h-3c-.621 0-1.125.504-1.125 1.125v3.75c0 .621.504 1.125 1.125 1.125h3c.621 0 1.125-.504 1.125-1.125V6.375z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 18.75a3.75 3.75 0 1 1 7.5 0 3.75 3.75 0 0 1-7.5 0z"
                  />
                </svg>
              </div>
              <div className="ml-4 flex-1">
                <h4 className="text-base font-semibold text-gray-900 dark:text-slate-100">Usuń z kursu</h4>
                <p className="mt-1 text-sm text-gray-600 dark:text-slate-400">
                  Trwale usuń użytkownika z kursu. Ta akcja nie może być cofnięta. Wszystkie dane związane z
                  uczestnictwem zostaną usunięte.
                </p>
                <div className="mt-4">
                  <PaziaButton
                    type="danger2"
                    title="Usuń z kursu"
                    btnType="button"
                    onClick={() => setRemoveModalOpen(true)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <MoveUserToCourseModal
        currentCourse={courseId}
        onCloseRequest={() => setModalRegistration(null)}
        registrationId={modalRegistration}
        userId={userId}
      />
      <RetainedRevenueModal
        id="remove-student-from-course"
        isOpen={removeModalOpen}
        title="Usuń użytkownika z kursu"
        description="Jesteś pewien że chcesz usunąć użytkownika z kursu? Wybierz, ile z wpłaconej kwoty ma zostać w przychodzie kursu."
        confirmText="Tak, usuń"
        loading={loadingRevenueSummary || !revenueSummary}
        amounts={revenueSummary}
        onCloseRequest={() => setRemoveModalOpen(false)}
        onConfirm={async (choice) => {
          try {
            await courseRegistrationApi.delete(courseId, registrationId, choice);
            showSuccess("Usunięto użytkownika z kursu");
            setRemoveModalOpen(false);
            invalidate();
            navigate(routes.STUDENT_COURSES(userId));
          } catch (e) {
            showError("Nie udało się usunąć użytkownika z kursu.");
            console.error(e);
          }
        }}
      />
    </>
  );
};
